import React, { useState } from 'react';
import { FaTimes, FaFolder, FaBullhorn, FaBookOpen, FaComments, FaCheck, FaShieldAlt, FaUserGraduate, FaUsers, FaFolderPlus } from 'react-icons/fa';
import { toast } from 'react-hot-toast';
import { ForumCategoriesAPI } from '../../utils/api';
import { getToken } from '../../utils/auth';

interface CreateCategoryModalProps {
    onClose: () => void;
    onSuccess: () => void;
    initialData?: any;
}

const CreateCategoryModal: React.FC<CreateCategoryModalProps> = ({ onClose, onSuccess, initialData }) => {
    const [name, setName] = useState(initialData?.name || '');
    const [label, setLabel] = useState(initialData?.label || '');
    const [description, setDescription] = useState(initialData?.description || '');
    const [section, setSection] = useState(initialData?.section || 'general');
    const [color, setColor] = useState(initialData?.color || 'blue');
    const [icon, setIcon] = useState(initialData?.icon || 'FaFolder'); // Default icon
    const [submitting, setSubmitting] = useState(false);

    const isEdit = !!initialData;

    const sections = [
        { id: 'admin', label: 'Khu Vực Điều Hành' },
        { id: 'study', label: 'Góc Học Tập' },
        { id: 'general', label: 'Thảo Luận Chung' }
    ];

    const colors = [
        { id: 'red', bg: 'bg-red-500' },
        { id: 'blue', bg: 'bg-blue-500' },
        { id: 'green', bg: 'bg-green-500' },
        { id: 'yellow', bg: 'bg-yellow-500' },
        { id: 'purple', bg: 'bg-purple-500' },
        { id: 'pink', bg: 'bg-pink-500' },
        { id: 'indigo', bg: 'bg-indigo-500' },
        { id: 'gray', bg: 'bg-gray-500' },
    ];

    const icons = [
        { id: 'FaFolder', component: <FaFolder /> },
        { id: 'FaFolderPlus', component: <FaFolderPlus /> },
        { id: 'FaBullhorn', component: <FaBullhorn /> },
        { id: 'FaBookOpen', component: <FaBookOpen /> },
        { id: 'FaComments', component: <FaComments /> },
        { id: 'FaShieldAlt', component: <FaShieldAlt /> },
        { id: 'FaUserGraduate', component: <FaUserGraduate /> },
        { id: 'FaUsers', component: <FaUsers /> },
    ];

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!name || !label) {
            toast.error('Vui lòng điền tên và tiêu đề');
            return;
        }

        // Validate name format
        if (!/^[a-z0-9_-]+$/.test(name)) {
            toast.error('Mã danh mục chỉ được chứa chữ thường, số, gạch ngang và gạch dưới');
            return;
        }

        try {
            setSubmitting(true);
            const token = getToken();
            if (!token) {
                toast.error('Vui lòng đăng nhập lại');
                return;
            }

            const data = {
                name,
                label,
                description,
                section,
                color,
                icon
            };

            if (isEdit) {
                await ForumCategoriesAPI.update(initialData.id, data, token);
                toast.success('Đã cập nhật danh mục');
            } else {
                await ForumCategoriesAPI.create(data, token);
                toast.success('Đã tạo danh mục mới');
            }

            onSuccess();
        } catch (error: any) {
            console.error('Save category error:', error);
            toast.error(error.message || 'Không thể lưu danh mục');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn" onClick={onClose}>
            <div
                className="bg-white dark:bg-gray-800 w-[95%] sm:w-full max-w-lg rounded-2xl shadow-xl overflow-hidden animate-slideUpIn max-h-[70vh] sm:max-h-[90vh] flex flex-col mt-12 sm:mt-0"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 shrink-0">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                        {isEdit ? 'Cập Nhật Chuyên Mục' : 'Tạo Chuyên Mục Mới'}
                    </h3>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                        <FaTimes />
                    </button>
                </div>

                {/* Body & Footer wrapped in form */}
                <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
                    {/* Scrollable Content */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-4">
                        {/* Name (ID) */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Mã danh mục (ID) <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value.toLowerCase().replace(/\s/g, '-'))}
                                placeholder="vd: thong-bao-lop"
                                className={`w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all dark:text-white ${isEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                                required
                                disabled={isEdit}
                            />
                            <p className="mt-1 text-xs text-gray-500">
                                {isEdit ? 'Không thể thay đổi mã danh mục' : 'Dùng cho URL, viết liền không dấu'}
                            </p>
                        </div>

                        {/* Label */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Tên hiển thị <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={label}
                                onChange={(e) => setLabel(e.target.value)}
                                placeholder="vd: Thông Báo Lớp"
                                className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all dark:text-white"
                                required
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Mô tả
                            </label>
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Mô tả ngắn về nội dung của chuyên mục này..."
                                rows={3}
                                className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all dark:text-white"
                            />
                        </div>

                        {/* Section */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Khu vực (Section)
                            </label>
                            <select
                                value={section}
                                onChange={(e) => setSection(e.target.value)}
                                className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all dark:text-white"
                            >
                                {sections.map(s => (
                                    <option key={s.id} value={s.id}>{s.label}</option>
                                ))}
                            </select>
                        </div>

                        {/* Icons */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Biểu tượng (Icon)
                            </label>
                            <div className="flex gap-2 flex-wrap bg-gray-50 dark:bg-gray-900 p-3 rounded-xl border border-gray-200 dark:border-gray-700">
                                {icons.map(IconItem => (
                                    <button
                                        key={IconItem.id}
                                        type="button"
                                        onClick={() => setIcon(IconItem.id)}
                                        className={`w-10 h-10 rounded-lg flex items-center justify-center text-xl transition-all ${icon === IconItem.id ? 'bg-blue-600 text-white shadow-md scale-105' : 'bg-white dark:bg-gray-800 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                                        title={IconItem.id}
                                    >
                                        {IconItem.component}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Color */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Màu sắc
                            </label>
                            <div className="flex gap-2 flex-wrap">
                                {colors.map(c => (
                                    <button
                                        key={c.id}
                                        type="button"
                                        onClick={() => setColor(c.id)}
                                        className={`w-8 h-8 rounded-full ${c.bg} flex items-center justify-center transition-transform hover:scale-110 ${color === c.id ? 'ring-2 ring-offset-2 ring-blue-500 dark:ring-offset-gray-800 scale-110' : ''}`}
                                    >
                                        {color === c.id && <FaCheck className="text-white text-xs" />}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Footer Actions - Fixed at bottom of form */}
                    <div className="flex gap-3 p-6 border-t border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 shrink-0">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 font-medium transition-colors"
                        >
                            Hủy bỏ
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {submitting ? 'Đang lưu...' : (isEdit ? 'Lưu Thay Đổi' : 'Tạo Chuyên Mục')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateCategoryModal;
